<script setup lang="ts">
import type { Channel, BackendError } from '~/types'
import type { operations } from '~/generated/types'

import axios from 'axios'

import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import Layout from '~/ui/Layout.vue'
import Modal from '~/ui/Modal.vue'
import Button from '~/ui/Button.vue'
import Input from '~/ui/Input.vue'
import Alert from '~/ui/Alert.vue'
import Gap from '~/ui/Gap.vue'
import Loader from '~/ui/Loader.vue'

const { channel } = defineProps<{ channel: Channel }>()

const emit = defineEmits<{
  created: [title: string]
}>()

const { t } = useI18n()

const isOpen = defineModel<boolean>()

const newAlbumTitle = ref<string>('')

const isLoading = ref(false)
const errors = ref<string[]>([])

const submit = async () => {
  isLoading.value = true
  errors.value = []

  try {
    // TODO: Fix the schema - this operation in practice returns 204 (no response), not 201!
    await axios.post<operations['create_album']['responses']['201']['content']['application/json']>('albums/', {
      title: newAlbumTitle.value,
      artist: channel.artist?.id
    })
    emit('created', newAlbumTitle.value)
    isOpen.value = false
    newAlbumTitle.value = '' // Optional: Reset the title for the next time it opens
  } catch (error) {
    errors.value = (error as BackendError).backendErrors
  } finally {
    isLoading.value = false
  }
}

defineExpose({
  submit
})
</script>

<template>
  <Modal
    v-model="isOpen"
    :title="channel?.artist?.content_category === 'podcast' ? t('components.channels.AlbumModal.header.newSeries') : t('components.channels.AlbumModal.header.newAlbum')"
    :cancel="t('components.channels.AlbumModal.button.cancel')"
    :maximize-size="false"
  >
    <template #alert>
      <Alert
        v-if="errors?.length > 0"
        red
      >
        <h4 class="header">
          {{ t('components.channels.AlbumForm.header.error') }}
        </h4>
        <ul class="list">
          <li
            v-for="(error, key) in errors"
            :key
          >
            {{ error }}
          </li>
        </ul>
      </Alert>
    </template>

    <Layout
      form
      @submit.stop.prevent="submit()"
    >
      <Gap />
      <Input
        v-model="newAlbumTitle"
        required
        type="text"
        :label="t('components.channels.AlbumForm.label.albumTitle')"
      />
    </Layout>

    <template #actions>
      <Button
        :disabled="newAlbumTitle.length === 0"
        class="color-primary-solid"
        @click.stop.prevent="submit()"
      >
        <Loader v-if="isLoading" />
        {{ t('components.channels.AlbumModal.button.create') }}
      </Button>
    </template>
  </Modal>
</template>
